import dayjs from "dayjs";
import { fmtL } from "../moneyData.js";

/**
 * Deep Analysis response -> the card shape the Money components expect.
 *
 * The API has no source for levers, dash, owner, conf, state, rank or cts,
 * so those are synthesised here and the components render unchanged.
 *
 * TOTAL by rule: never throws, never returns undefined for
 * problems / dash / levers / proof. The UI maps those without guards, so an
 * undefined white-screens the page.
 *
 * Money is formatted in ₹, never converted — the payload is already rupees.
 */

// --- taxonomy -------------------------------------------------------------

/**
 * The API has 5 modules, the UI 4 categories. An unmapped value renders a
 * blank label and an unstyled chip. `returns` folds into `recon`; give it its
 * own chip by adding to CAT_LABEL, MOD_NAME and money.css.
 */
const MODULE_MAP = {
  inventory: "inventory",
  profitability: "profit",
  profit: "profit",
  ads: "ads",
  advertising: "ads",
  payments: "recon",
  payment: "recon",
  returns: "recon",
  reconciliation: "recon",
  recon: "recon",
};

const DEFAULT_MODULE = "profit";

/**
 * Only these four `state` values have CSS (money.css:145-148). Derived from the
 * module because the API does not describe a problem's "shape" of loss.
 */
const STATE_BY_MODULE = {
  recon: { state: "owed", stateLabel: "Owed" },
  inventory: { state: "trapped", stateLabel: "Trapped" },
  ads: { state: "losing", stateLabel: "Losing" },
  profit: { state: "losing", stateLabel: "Losing" },
};

// --- small helpers --------------------------------------------------------

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

/**
 * Response shape, verified against a real completed run:
 *
 *   top level : analysis_id, status, modules[], module_status{}, start_date,
 *               end_date, problems[], errors{}, total_cost_in_usd, created_at
 *   problem   : tag, problem_title, problem_description,
 *               total_potential_savings, affected_items[], solutions[]
 *   item      : { name, potential_savings }
 *   solution  : { title, description }
 *
 * Real names come first in each pick(); the looser variants behind them mean
 * a backend rename loses one field, not the whole card.
 */

/** Reads the first key that is actually present. */
function pick(source, keys, fallback) {
  if (!isObj(source)) return fallback;
  for (const k of keys) {
    const v = source[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
}

/** Coerces "₹5.88M", "5,880,000", 5880000 -> 5880000. Returns 0, never NaN. */
function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const cleaned = value.replace(/[^0-9.kKmMbBcrCRlL]/g, "");
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num)) return 0;

  // Honour a magnitude suffix if the backend sends display strings.
  if (/cr/i.test(value)) return num * 10000000;
  if (/\dl/i.test(value) || /\bl\b/i.test(value)) return num * 100000;
  if (/m/i.test(value)) return num * 1000000;
  if (/k/i.test(value)) return num * 1000;
  if (/b/i.test(value)) return num * 1000000000;
  return num;
}

/**
 * ₹ via the module's fmtL, plus two guards kept out of the shared helper:
 * 0 renders "₹0" not "₹0L", and sub-lakh amounts render in full.
 */
export function toRupee(n) {
  const v = Math.abs(toNumber(n));
  if (v === 0) return "₹0";
  if (v < 100000) return `₹${Math.round(v).toLocaleString("en-IN")}`;
  return `₹${fmtL(v)}`;
}

/** Stable id — this is both the React key AND the drill/dashboard lookup key,
 *  so it must not change between polls. Deliberately NOT array-index based. */
function makeId(problem, moduleKey, index) {
  const explicit = pick(problem, ["id", "problem_id", "key"], null);
  if (explicit) return String(explicit);

  // `problem_title` is the real field. Without it the slug came out empty and
  // every id degraded to the index fallback ("inventory-0"), which is exactly
  // the unstable-across-runs behaviour this function exists to avoid.
  const title = String(
    pick(problem, ["problem_title", "title", "problem", "summary"], "")
  );
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  return slug ? `${moduleKey}-${slug}` : `${moduleKey}-${index}`;
}

/** "/mo" when the analysed window is roughly a month, matching the dummy data. */
function derivePeriod(startDate, endDate) {
  if (!startDate || !endDate) return "";
  const days = dayjs(endDate).diff(dayjs(startDate), "day");
  if (days >= 26 && days <= 32) return "/mo";
  if (days > 0) return ` /${days}d`;
  return "";
}

// --- marketplaces / items -> proof rows -----------------------------------

/**
 * Affected marketplaces map naturally onto the existing proof shape, which is
 * an array of [label, value] pairs — e.g. ["Ajio", "₹58.8L"].
 */
/**
 * The affected list, normalised. One source for the proof rows, the dashboard
 * table and the count, so the three can never disagree.
 * Names render verbatim — the list mixes SKUs and whole marketplaces.
 */
function getAffectedList(problem) {
  const raw =
    pick(problem, ["affected_items", "items", "skus"], null) ||
    pick(problem, ["affected_marketplaces", "marketplaces", "channels"], null) ||
    [];

  return Array.isArray(raw)
    ? raw
    : isObj(raw)
    ? Object.entries(raw).map(([k, v]) => ({ name: k, amount: v }))
    : [];
}

function buildProof(problem) {
  const list = getAffectedList(problem);

  const rows = list.map((entry) => {
    if (Array.isArray(entry)) return [String(entry[0]), toRupee(entry[1])];
    if (!isObj(entry)) return [String(entry), ""];
    const label = pick(
      entry,
      ["name", "marketplace", "channel", "item", "sku", "label"],
      "—"
    );
    const amount = pick(
      entry,
      ["potential_savings", "amount", "impact", "value", "savings", "gap"],
      null
    );
    return [String(label), amount === null ? "" : toRupee(amount)];
  });

  // Match the dummy data's "top 4 + rollup" presentation.
  if (rows.length > 5) {
    const rest = rows.slice(4);
    const restTotal = rest.reduce(
      (sum, [, val]) => sum + toNumber(val),
      0
    );
    return [
      ...rows.slice(0, 4),
      [`+ ${rest.length} more`, restTotal ? toRupee(restTotal) : ""],
    ];
  }
  return rows;
}

// --- recommended actions -> levers ----------------------------------------

/**
 * Recommended actions become the choices shown in DrillDetail.
 * tier is "bronze" — "you confirm it, we can't track it" — because nothing
 * verifies an AI suggestion. Always returns an array; the UI maps it unguarded.
 */
function buildLevers(problem, id, problemSavings = 0) {
  const raw = pick(
    problem,
    ["solutions", "recommended_actions", "recommendations", "actions", "next_steps"],
    []
  );
  const list = Array.isArray(raw) ? raw : [];

  return list.map((entry, i) => {
    // A real solution is { title, description } — e.g.
    //   title:       "Bulk Update Costs"
    //   description: "Prioritize the top 50 SKUs listed above and upload …"
    // The title is the pickable option; the description is the instruction, and
    // losing it would strip the recommendation of everything actionable.
    const heading = isObj(entry)
      ? String(pick(entry, ["title", "action", "text"], ""))
      : String(entry || "");
    const detail = isObj(entry)
      ? String(pick(entry, ["description", "detail"], ""))
      : "";
    const text = heading || detail;

    const impact = isObj(entry)
      ? toNumber(pick(entry, ["impact", "savings", "potential_savings"], 0))
      : 0;

    // Short label for the card, full text for the description.
    const shortLabel = text.length > 64 ? `${text.slice(0, 61)}…` : text;

    return {
      id: `${id}-lever-${i}`,
      t: shortLabel || `Recommendation ${i + 1}`,
      speed: "",
      tier: "bronze",
      owner: "UN", // OWNERS.UN ("Unassigned") already exists — moneyData.js:48
      impact,
      // Prefer the instruction; fall back to the heading when there is no
      // separate description, so this is never blank.
      d: detail || heading,
      /**
       * The API quantifies savings per PROBLEM (`total_potential_savings`),
       * never per solution. Rather than "Impact not quantified", which reads as
       * a gap, state the finding's total and be explicit that these solutions
       * share it — claiming the full amount for one of several options would
       * overstate it.
       */
      impactTxt: impact
        ? `Potential recovery of <b>${toRupee(impact)}</b>.`
        : problemSavings
        ? `Contributes to the <b>${toRupee(problemSavings)}</b> identified for ` +
          `this finding${list.length > 1 ? ", shared across these options" : ""}.`
        : "Impact not quantified by the analysis.",
      // Prefills the Decision box (DrillDetail.js:117). It must be the actual
      // instruction — prefilling it with the heading gave the user a decision
      // note that just repeated the option's own name.
      action: detail || heading,
      verS: "You confirm the outcome — this recommendation isn't auto-tracked.",
    };
  });
}

// --- dashboard table ------------------------------------------------------

/**
 * Dashboard.js reads dash.cols / dash.rows with NO null guard, so this always
 * returns a well-formed object even when there is nothing to show.
 */
function buildDash(problem) {
  // Same source as the proof rows, but UNCAPPED — "see the data" is exactly
  // where the user goes to read every affected item, so it must not be the
  // truncated top-4 view.
  const list = getAffectedList(problem);

  if (!list.length) return { cols: [], rows: [] };

  const rows = list.map((entry) => {
    if (Array.isArray(entry)) return entry.map(String);
    if (!isObj(entry)) return [String(entry), ""];
    const label = pick(
      entry,
      ["name", "marketplace", "channel", "item", "sku", "label"],
      "—"
    );
    const amount = pick(entry, ["potential_savings", "amount", "impact", "value", "savings", "gap"], null);
    return [String(label), amount === null ? "—" : toRupee(amount)];
  });

  return { cols: ["Affected", "Impact"], rows };
}

// --- main adapters --------------------------------------------------------

/**
 * @returns {{ problems: object[], moduleErrors: object[], totals: object }}
 *          Always well-formed, even for a malformed payload.
 */
export function adaptAnalysisToProblems(analysis) {
  const empty = { problems: [], moduleErrors: [], totals: {} };
  if (!isObj(analysis)) return empty;

  try {
    const startDate = pick(analysis, ["start_date", "startDate"], null);
    const endDate = pick(analysis, ["end_date", "endDate"], null);
    const period = derivePeriod(startDate, endDate);
    const createdAt = pick(analysis, ["created_at", "createdAt"], null);
    // Carried onto every problem so "Ask Helix" can cite the exact run rather
    // than describing it in prose — see utils/askHelix.js.
    const analysisId = pick(analysis, ["analysis_id", "analysisId", "id"], null);

    // Modules that failed to process — surfaced as a warning strip so a partial
    // brief isn't misread as "we found nothing there".
    // `module_status` is the per-module map. NOTE the sibling `modules` is an
    // ARRAY of module names, so it must not be listed first here — pick()
    // returns the first present key and an array fails the isObj() test below,
    // which silently disabled the whole partial-failure warning.
    const moduleStatuses =
      pick(analysis, ["module_status", "module_statuses"], null) || {};
    const moduleErrors = [];
    if (isObj(moduleStatuses)) {
      Object.entries(moduleStatuses).forEach(([mod, val]) => {
        const status = isObj(val) ? pick(val, ["status"], "") : val;
        const err = isObj(val) ? pick(val, ["error", "message"], "") : "";
        if (String(status).toLowerCase() === "failed" || err) {
          moduleErrors.push({ module: mod, message: err || "Processing failed" });
        }
      });
    }
    // A real run returns `errors` as an OBJECT keyed by module ({} when clean),
    // not an array — handle both so neither shape is silently dropped.
    const processingErrors = pick(analysis, ["errors", "processing_errors"], null);
    if (Array.isArray(processingErrors)) {
      processingErrors.forEach((e) =>
        moduleErrors.push({
          module: isObj(e) ? pick(e, ["module"], "—") : "—",
          message: isObj(e) ? pick(e, ["error", "message"], "Error") : String(e),
        })
      );
    } else if (isObj(processingErrors)) {
      Object.entries(processingErrors).forEach(([mod, e]) => {
        if (!e) return;
        moduleErrors.push({
          module: mod,
          message: isObj(e) ? pick(e, ["error", "message"], "Error") : String(e),
        });
      });
    }

    const rawProblems =
      pick(analysis, ["problems", "issues", "findings", "results"], []) || [];
    const list = Array.isArray(rawProblems) ? rawProblems : [];

    const problems = list.map((p, index) => {
      // "tag" is the real field. Without it every problem fell through to the
      // DEFAULT_MODULE, which is why all 20 cards showed the PROFIT chip.
      const rawModule = String(
        pick(p, ["tag", "module", "category", "source"], "")
      ).toLowerCase();
      const moduleKey = MODULE_MAP[rawModule] || DEFAULT_MODULE;
      const id = makeId(p, moduleKey, index);

      const amtNum = toNumber(
        pick(
          p,
          [
            "total_potential_savings",
            "potential_savings",
            "potentialSavings",
            "savings",
            "impact",
            "amount",
            "gap",
          ],
          0
        )
      );

      const affected = getAffectedList(p);
      const proof = buildProof(p);
      const title = String(
        pick(
          p,
          ["problem_title", "title", "problem", "summary", "name"],
          "Untitled finding"
        )
      );
      const description = String(
        pick(p, ["problem_description", "description", "detail", "sub"], "")
      );

      // Count the FULL list, not `proof` — proof is capped at 4 rows plus a
      // "+ N more" rollup, so using its length reported "5 affected" for a
      // problem touching eleven things.
      const marketplaceCount = affected.length;
      const stateInfo = STATE_BY_MODULE[moduleKey] || STATE_BY_MODULE.profit;

      return {
        id,
        rank: index + 1,
        module: moduleKey,
        ...stateInfo,
        owner: "UN",
        title,
        // Falls back to a synthesised summary so the card is never bare.
        sub:
          description ||
          (marketplaceCount
            ? `${marketplaceCount} item${marketplaceCount > 1 ? "s" : ""} affected`
            : "Identified by deep analysis"),
        // Set later, on the single largest problem only.
        oneThing: null,
        amt: toRupee(amtNum),
        amtNum,
        period,
        // Always set — the card hides this line rather than inventing one.
        metric: marketplaceCount
          ? `${marketplaceCount} item${marketplaceCount > 1 ? "s" : ""} affected`
          : "From deep analysis",
        conf: moduleErrors.some((e) => e.module === rawModule) ? "med" : "high",
        status: "open",
        cAt: createdAt ? dayjs(createdAt).format("D MMM · h:mm A") : "",
        cts: createdAt ? new Date(createdAt).getTime() : Date.now() - index,
        /**
         * The analysis window, carried on every problem so the drill and
         * dashboard views can deep-link a SKU into the profit table over the
         * SAME period the finding was calculated on. Threading it as a prop
         * through index.js -> Brief -> DrillDetail instead would touch four
         * components to move one immutable value.
         */
        startDate,
        endDate,
        /**
         * Identifiers Helix can quote back. `sourceTag` is the API's own
         * `tag` ("returns", "payments"), kept alongside our 4-category
         * `module` because the analysis speaks in 5 tags and collapsing
         * "returns" into "recon" loses which one it actually was.
         */
        analysisId,
        sourceTag: rawModule || null,
        // The list mixes marketplaces and individual SKUs, so "marketplaces"
        // was the wrong heading for rows like "SKU_107 (Blinkit)".
        proofHead: "Affected items",
        proof,
        dash: buildDash(p),
        levers: buildLevers(p, id, amtNum),
      };
    });

    // Highest-value problem gets the ★ THE ONE THING badge. Exactly one — the
    // badge draws a red border (money.css:366) and loses all meaning if every
    // card has it.
    if (problems.length) {
      const top = problems.reduce((a, b) => (b.amtNum > a.amtNum ? b : a));
      top.oneThing = "Recommended to act on this first.";
    }

    return {
      problems,
      moduleErrors,
      totals: {
        startDate,
        endDate,
        createdAt,
        updatedAt: pick(analysis, ["updated_at", "updatedAt"], null),
        costUsd: toNumber(
          pick(
            analysis,
            [
              "total_cost_in_usd",
              "total_cost_usd",
              "total_cost",
              "cost_usd",
              "cost",
            ],
            0
          )
        ),
        totalSavings: problems.reduce((sum, p) => sum + p.amtNum, 0),
      },
    };
  } catch (err) {
    // Total by contract — a bad payload must not take the page down.
    console.error("[deepAnalysisAdapter] Failed to adapt analysis:", err);
    return empty;
  }
}

/** History rows for the "Previous runs" dropdown. */
export function adaptHistory(list) {
  if (!Array.isArray(list)) return [];
  try {
    return list
      .filter(isObj)
      .map((row) => {
        const at = pick(row, ["created_at", "createdAt"], null);
        return {
          id: String(pick(row, ["analysis_id", "analysisId", "id"], "")),
          status: String(pick(row, ["status"], "unknown")).toLowerCase(),
          at,
          atLabel: at ? dayjs(at).format("D MMM · h:mm A") : "—",
          costUsd: toNumber(
            pick(row, ["total_cost_usd", "total_cost", "cost_usd", "cost"], 0)
          ),
          modules: pick(row, ["modules"], []),
        };
      })
      .filter((r) => r.id)
      .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0)); // newest first
  } catch (err) {
    console.error("[deepAnalysisAdapter] Failed to adapt history:", err);
    return [];
  }
}
